import type { TranslationTree } from '../../types';

export const traceability: TranslationTree = {
  traceability: {
    formPage: {
      hero: {
        badge: 'Fiche de traçabilité',
        title: "Validez l'outil puis consignez vos relevés",
        description:
          "Vérifiez les informations de l'outil scanné, confirmez son état et renseignez les paramètres de production pour assurer une traçabilité complète.",
        breadcrumb: 'Données de traçabilité',
      },
      invalid: {
        title: 'Outil introuvable',
        description:
          "L'identifiant fourni ne correspond à aucun outil enregistré. Vérifiez qu'il ne s'agit pas d'une erreur de lecture ou de saisie.",
        backButton: 'Retour au scan',
      },
      steps: {
        badge: 'Étapes de saisie',
        title: 'Renseignez les conditions de production',
        description:
          'Complétez les sections ci-dessous pour enregistrer un nouveau relevé. Vos informations seront validées étape par étape.',
      },
    },
    toolInfo: {
      badge: "Validation de l'outil",
      actions: {
        close: 'Fermer',
      },
      loading: 'Récupération des informations en cours…',
      errorDefault: 'Erreur lors de la récupération des données.',
      fields: {
        reference: 'Référence',
        company: 'Entreprise',
        totalTraces: 'Relevés enregistrés',
        latestStatus: 'Statut récent',
      },
      fallback: {
        notProvided: 'Non renseignée',
      },
      latestReasonPrefix: 'Motif récent :',
    },
    scanPage: {
      hero: {
        badge: 'Session de scan',
        title: 'Identifiez vos outils en un instant',
        description:
          "Positionnez l'outil face à la caméra pour détecter automatiquement son identifiant ou saisissez-le manuellement si nécessaire.",
        breadcrumb: 'Scanner',
      },
      tabs: {
        camera: 'Caméra',
        keyence: 'Keyence SR-X300',
      },
      scan: {
        title: 'Scan automatique',
        description:
          "Tenez l'outil devant la caméra. La lecture déclenche automatiquement la fiche de traçabilité.",
        overlay: {
          detected: 'Outil détecté : {{id}}',
          waiting: "En attente de détection… maintenez l'outil stable.",
          active: 'Scan actif',
        },
        tips: {
          lighting:
            'Utilisez un fond neutre et évitez les reflets pour accélérer la lecture du code.',
          switchCamera:
            'La bascule de caméra est disponible en bas à droite si vous scannez depuis un mobile.',
        },
      },
      manual: {
        title: "Saisie manuelle de l'identifiant",
        description:
          "Choisissez l'outil dans la liste ou entrez l'ID présent sur son marquage.",
        label: 'ID outil',
        placeholder: 'Ex. TL-4823',
        submit: "Valider l'ID",
      },
      dev: {
        title: 'Mode développement',
        description: 'Bypassez le scan pour tester rapidement la fiche de traçabilité sans caméra.',
        button: 'Ouvrir la fiche de test',
      },
      keyence: {
        title: 'Scanner Keyence SR-X300',
        description:
          'Configurez la caméra intelligente et déclenchez un scan pour lire le code DataMatrix.',
        backendLabel: 'URL du backend',
        ipLabel: 'Adresse IP de la caméra',
        backendPlaceholder: 'ex. https://toollife-datamatrix.local:44312',
        ipPlaceholder: 'ex. 192.168.100.100',
        connect: 'Connecter',
        disconnect: 'Déconnecter',
        connecting: 'Connexion…',
        disconnecting: 'Déconnexion…',
        scan: 'Scanner',
        scanning: 'Scan en cours…',
        cancel: 'Annuler',
        detected: 'Outil détecté',
        accept: 'Accepter et continuer',
        error: 'Erreur',
        status: {
          connected: 'Connecté à la caméra',
          disconnected: 'Non connecté',
        },
        errors: {
          connectionFailed: 'Échec de la connexion à la caméra',
          disconnectionFailed: 'Échec de la déconnexion de la caméra',
          scanFailed: 'Échec du scan',
          cancelFailed: "Échec de l'annulation du scan",
          networkError: "Erreur réseau - vérifiez l'URL du backend",
        },
        tips: {
          network: 'Assurez-vous que la caméra Keyence est connectée au même réseau.',
          position: "Positionnez l'outil dans le champ de vision de la caméra avant de déclencher.",
          backend: 'Le service backend gère la communication avec la caméra.',
        },
        advanced: {
          title: 'Paramètres avancés',
        },
        info: {
          title: 'Fonctionnement',
          description: 'Le Keyence SR-X300 lit les codes DataMatrix à distance.',
          step1: "Entrez l'URL du backend et l'adresse IP de la caméra.",
          step2: 'Cliquez sur "Connecter" pour établir la connexion avec la caméra.',
          step3: 'Appuyez sur "Scanner" pour capturer et décoder l\'identifiant.',
        },
      },
      camera: {
        title: 'Scan automatique',
        description:
          "Maintenez l'outil devant la caméra. Une fois détecté, le formulaire de traçabilité s'ouvre automatiquement.",
        detected: 'Outil détecté',
        scanning: 'Scan en cours…',
        tips: {
          lighting:
            'Utilisez un fond neutre et évitez les reflets pour accélérer la lecture du code.',
          position: "Gardez l'outil stable dans le champ de vision de la caméra.",
        },
      },
      addTool: {
        button: 'Créer un outil',
      },
    },
    machineManager: {
      title: 'Gestion des machines',
      description:
        'Ajoutez les machines de votre parc, mettez à jour leurs caractéristiques et synchronisez la sélection avec le formulaire.',
      confirmDelete: 'Confirmer',
      errors: {
        nameRequired: "L'identifiant de la machine est requis.",
        selectBeforeDelete: 'Sélectionnez une machine à supprimer.',
        notFound: "La machine sélectionnée n'existe plus.",
        apiNotReady: 'API non prête',
        fetchFailed: 'Échec du chargement des machines',
        saveFailed: "Échec de l'enregistrement de la machine",
        deleteFailed: "La suppression de machine n'est pas encore implémentée.",
        noData: 'Aucune donnée de machine retournée',
      },
      form: {
        sectionTitle: 'Fiche machine',
        fields: {
          name: {
            label: 'Identifiant de la machine',
            placeholder: 'ex : DMG Mori DMC 65 monoBLOCK',
          },
          brand: {
            label: 'Marque',
            placeholder: 'ex : DMG Mori',
          },
          model: {
            label: 'Modèle',
            placeholder: 'Saisir le modèle',
          },
          serial: {
            label: 'Numéro de série',
            placeholder: 'Identifiant interne ou constructeur',
          },
          kafkaName: {
            label: 'Source des signaux',
            placeholder: 'Aucune source de signaux',
            help: 'La mesure de la machine dans la base de signaux (InfluxDB). La page Signaux et les prédictions en direct la lisent.',
            groupActive: 'Actives',
            groupHistory: 'Historique seulement',
            historyOnlyWarning:
              "Aucune donnée dans la dernière heure : les prédictions en direct liront la dernière pièce enregistrée et indiqueront son âge.",
            loadError: "La liste des sources de signaux n'a pas pu être chargée.",
            retry: 'Réessayer',
          },
        },
        submit: {
          save: 'Enregistrer',
          update: 'Mettre à jour',
          saving: 'Enregistrement...',
        },
        new: 'Nouvelle fiche',
      },
      helper:
        'Les machines sont enregistrées localement pour faciliter la saisie. Mettez à jour les informations en cas de maintenance ou de modification.',
      list: {
        title: 'Machines enregistrées',
        count: '{{count}} élément(s)',
        empty: "Aucune machine enregistrée pour l'instant.",
        serial: 'SN : {{serial}}',
        sourceBadgeTitle: 'Source des signaux',
      },
      delete: {
        button: 'Supprimer la sélection',
        deleting: 'Suppression...',
      },
    },
    processManager: {
      form: {
        sectionTitle: 'Fiche processus',
        fields: {
          name: {
            label: 'Nom du processus',
            placeholder: 'ex. Finition EGO2-40',
          },
          type: {
            label: 'Type de processus',
            standard: 'Standard',
            test: 'Test',
          },
          toolReference: {
            label: "Référence outil",
            placeholder: '— sélectionner —',
            noOptions: 'Aucune référence outil disponible',
          },
          material: {
            label: 'Matière',
            none: '— aucune —',
          },
          materialLot: {
            label: 'Lot matière',
            none: 'Aucun lot',
            placeholder: '— sélectionner un lot —',
            disabled: "Sélectionnez d'abord une matière",
          },
          article: {
            label: 'Article',
            none: '— aucun —',
          },
          machines: {
            label: 'Machines',
            placeholder: '— ajouter une machine —',
          },
        },
        showToOperator: "Afficher à l'opérateur",
        durationWarning: "Aucune durée d'usinage n'est préremplie — la durée par pièce sera demandée à l'opérateur à chaque traçabilité utilisant ce process.",
        submit: {
          save: 'Enregistrer le processus',
          update: 'Enregistrer les modifications',
          saving: 'Enregistrement...',
          duplicate: 'Dupliquer',
          duplicateHint: 'Copier ce processus sans enregistrer',
        },
        new: 'Nouveau processus vide',
        editingPill: 'Modification — {{name}}',
        notSet: '— Non défini —',
        sections: {
          cuttingParameters: {
            title: 'Paramètres de coupe',
            toggleLabel: 'Afficher/masquer la section paramètres de coupe',
          },
          machinedResults: {
            title: 'Résultats d\'usinage',
            toggleLabel: 'Afficher/masquer la section résultats d\'usinage',
          },
          operatorInfo: {
            title: 'Infos opérateur',
            toggleLabel: 'Afficher/masquer la section infos opérateur',
          },
          tooling: {
            title: 'Outillage & bridage',
            toggleLabel: 'Afficher/masquer la section outillage & bridage',
          },
          countLabel: '{{set}}/{{total}} renseigné(s)',
        },
        prefillFields: {
          ae: {
            label: 'ae',
            placeholder: 'ex. 2.5',
            unit: 'mm',
          },
          ap: {
            label: 'ap',
            placeholder: 'ex. 5.0',
            unit: 'mm',
          },
          vc: {
            label: 'vc',
            placeholder: 'ex. 120',
            unit: 'm/min',
          },
          fz: {
            label: 'fz',
            placeholder: 'ex. 0.05',
            unit: 'mm/dent',
          },
          machinedVolume: {
            label: 'Volume usiné',
            placeholder: 'ex. 10',
            unit: 'cm³',
          },
          machinedDistance: {
            label: 'Distance usinée',
            placeholder: 'ex. 50',
            unit: 'm',
          },
          machinedPieces: {
            label: 'Pièces usinées',
            placeholder: 'ex. 100',
            unit: 'pcs',
          },
          totalMachiningDuration: {
            label: 'Durée totale d\'usinage',
            placeholder: 'ex. 3600',
            unit: 's',
          },
          pieceMachiningDuration: {
            label: 'Durée par pièce',
            placeholder: 'ex. 36',
            unit: 's',
          },
          malround: {
            label: 'Malround',
            placeholder: 'ex. 5',
            unit: 'µm',
          },
          operator: {
            label: 'Opérateur',
            placeholder: 'ex. J. Dupont',
          },
          operation: {
            label: 'Opération',
            placeholder: 'ex. Passe de finition',
          },
          lubType: {
            label: 'Type de lubrification',
            placeholder: 'ex. Émulsion',
          },
          lubBrand: {
            label: 'Marque lubrifiant',
            placeholder: 'ex. Blaser B-Cool',
          },
          holderBrand: {
            label: 'Marque porte-outil',
            placeholder: 'ex. REGO-FIX',
          },
          holderType: {
            label: 'Type de porte-outil',
            placeholder: 'ex. Pince de serrage',
          },
          clampingType: {
            label: 'Type de bridage',
            placeholder: 'ex. Étau',
          },
          clampingBrand: {
            label: 'Marque bridage',
            placeholder: 'ex. Hainbuch',
          },
        },
      },
      list: {
        title: 'Processus enregistrés',
        count: '{{count}} processus',
        empty: 'Aucun processus enregistré pour l\'instant.',
        showArchived: 'Afficher les archivés',
        archivedBadge: 'Archivé',
        searchPlaceholder: 'Rechercher par nom…',
        filterAll: 'Tous',
        filterStandard: 'Standard',
        filterTest: 'Test',
      },
      archive: {
        button: 'Archiver',
        confirm: "Confirmer l'archivage",
        archiving: 'Archivage...',
      },
      restore: {
        button: 'Restaurer',
      },
      errors: {
        apiNotReady: 'API non prête',
        fetchFailed: 'Échec du chargement des processus',
        noData: 'Aucune donnée processus retournée',
        nameRequired: 'Le nom du processus est requis.',
        toolReferenceRequired: 'La référence outil est requise.',
        saveFailed: "Échec de l'enregistrement du processus",
      },
    },
    processSelection: {
      title: 'Choisir un processus pour pré-remplir les champs connus',
      skip: 'Commencer vide',
      prefilled: '{{count}} champs pré-remplis',
      youFill: '{{count}} à remplir',
      type: {
        standard: 'Standard',
        test: 'Test',
      },
      chip: {
        label: 'Modèle',
        hidden: '{{count}} champs masqués',
        change: 'Changer',
        clear: 'Effacer',
      },
      loading: 'Chargement des modèles de processus…',
      errorTitle: 'Impossible de charger les modèles de processus',
      errorDescription: 'Vous pouvez toujours enregistrer une trace en remplissant le formulaire manuellement.',
      errorRetry: 'Réessayer',
      sections: {
        cutting: 'Paramètres de coupe',
        machined: 'Résultats usinés',
        operatorInfo: 'Opérateur & opération',
        machine: 'Machine',
        tooling: 'Porte-outil & bridage',
        material: 'Matière',
        toolState: 'État de l\'outil',
      },
      machineFallback: 'Sélectionner une machine…',
      emptyColumn: 'Aucun processus de ce type',
    },
    materialManager: {
      form: {
        sectionTitle: 'Fiche matière',
        fields: {
          name: {
            label: 'Nom de la matière',
            placeholder: 'ex. X5CrNi18-10 Inox',
          },
          isoCategory: {
            label: 'Catégorie ISO',
            placeholder: '— sélectionner —',
          },
          designation: {
            label: 'Désignation',
            placeholder: 'ex. X5CrNi18-10',
          },
          description: {
            label: 'Description',
            placeholder: 'Notes optionnelles sur cette matière',
          },
        },
        submit: {
          save: 'Enregistrer',
          update: 'Mettre à jour',
          saving: 'Enregistrement...',
        },
        new: 'Nouvelle matière',
      },
      list: {
        title: 'Matières enregistrées',
        count: '{{count}} matière(s)',
        empty: 'Aucune matière enregistrée pour l\'instant.',
        emptyTitle: 'Aucune matière',
        emptyHint: 'Ajoutez la première matière pour constituer votre catalogue.',
        showArchived: 'Afficher les archivées',
        archivedBadge: 'Archivée',
      },
      archive: {
        button: 'Archiver',
        confirm: "Confirmer l'archivage",
        archiving: 'Archivage...',
      },
      restore: {
        button: 'Restaurer',
      },
      errors: {
        apiNotReady: 'API non prête',
        fetchFailed: 'Échec du chargement des matières',
        nameRequired: 'Le nom de la matière est requis.',
        isoCategoryRequired: 'La catégorie ISO est requise.',
        saveFailed: "Échec de l'enregistrement de la matière",
        noData: 'Aucune donnée matière retournée',
      },
    },
    articleManager: {
      form: {
        sectionTitle: 'Fiche article',
        fields: {
          name: {
            label: "Nom de l'article",
            placeholder: 'ex. Bride aluminium 40mm',
          },
        },
        submit: {
          save: 'Enregistrer',
          update: 'Mettre à jour',
          saving: 'Enregistrement...',
        },
        new: 'Nouvel article',
      },
      list: {
        title: 'Articles enregistrés',
        count: '{{count}} article(s)',
        empty: "Aucun article enregistré pour l'instant.",
        showArchived: 'Afficher les archivés',
        archivedBadge: 'Archivé',
      },
      archive: {
        button: 'Archiver',
        confirm: "Confirmer l'archivage",
        archiving: 'Archivage...',
      },
      restore: {
        button: 'Restaurer',
      },
      errors: {
        apiNotReady: 'API non prête',
        fetchFailed: 'Échec du chargement des articles',
        nameRequired: "Le nom de l'article est requis.",
        saveFailed: "Échec de l'enregistrement de l'article",
        noData: 'Aucune donnée article retournée',
      },
    },
    form: {
      stepCounter: 'Étape {{current}} sur {{total}}',
      sending: 'Envoi en cours…',
      cancel: "Annuler l'envoi",
      errorDefault: 'Une erreur est survenue lors de la récupération des données.',
      missingFields: "{{count}} champ(s) doivent être remplis avant d'envoyer.",
      missingFieldsNamed: "Champs requis manquants : {{fields}}",
      scanPopup: {
        title: 'Scanner un outil',
        description: "Présentez l'outil devant la caméra pour détecter automatiquement son identifiant.",
        helper: 'Gardez le marquage net et bien éclairé pour accélérer la lecture.',
        status: {
          waiting: "En attente de détection… gardez l'outil immobile.",
          detected: 'Outil détecté : {{id}}',
        },
        actions: {
          cancel: 'Annuler',
        },
      },
      retry: 'Réessayer',
      backHome: "Retour à l'accueil",
      success: 'Relevé transmis avec succès.',
      newScan: 'Nouveau scan',
      previous: 'Précédent',
      next: 'Suivant',
      submit: 'Valider',
      stepAriaLabel: 'Étape {{number}} : {{label}}, {{status}}',
      stepStatusCompleted: 'terminée',
      stepStatusCurrent: 'en cours',
      stepStatusUpcoming: 'à venir',
      validation: {
        requiredMultiTooltip: "Au moins un des champs marqués d'un * jaune doit être rempli.",
        requiredMultiAtLeastOne: 'Au moins un champ doit être rempli.',
        checkboxAtLeastOne: 'Veuillez sélectionner au moins une option.',
      },
    },
    steps: {
      general: 'Général',
      material: 'Matière',
      machine: 'Machine',
      holder: 'Porte-outil',
      clamping: 'Serrage pièce',
      lubrication: 'Lubrification',
      break: 'État outil',
    },
    generalTab: {
      durationsTitle: 'Durées de cycle',
      durationsDescription:
        "Renseignez la durée totale ou par pièce pour contextualiser l'usinage.",
      durationsRequiredCounter: '1 sur 2 requis',
      totalDurationLabel: 'Durée totale',
      pieceDurationLabel: 'Durée par pièce',
      duration: {
        hoursShort: 'h',
        hoursLabel: 'heures',
        minutesShort: 'min',
        minutesLabel: 'minutes',
        secondsShort: 's',
        secondsLabel: 'secondes',
        increase: 'Augmenter les {{unit}}',
        decrease: 'Diminuer les {{unit}}',
      },
      productionTitle: 'Production réalisée',
      productionDescription: 'Indiquez les volumes produits pendant ce relevé.',
      productionRequiredCounter: '1 sur 3 requis',
      piecesLabel: 'Pièces usinées',
      volumeLabel: 'Volume retiré [cm³]',
      distanceLabel: 'Distance parcourue [m]',
      qualityTitle: 'Qualité et opérateur',
      qualityDescription: 'Suivez les mesures de qualité et la personne en charge.',
      malroundLabel: 'Malrond (µm)',
      malroundPlaceholder: 'Ex. 12',
      operationLabel: 'Opération',
      operationPlaceholder: 'Sélectionner ou saisir une opération…',
      operatorLabel: 'Opérateur référent',
      cutParameters: {
        title: 'Paramètres de coupe',
        description: 'Conditions de coupe optionnelles pour cette opération.',
        aeLabel: 'ae (mm)',
        apLabel: 'ap (mm)',
        vcLabel: 'vc (m/min)',
        fzLabel: 'fz (mm/z)',
        placeholder: '–',
      },
    },
    materialTab: {
      materialTitle: 'Matière usinée',
      materialDescription:
        'Sélectionnez la matière depuis le catalogue pour affiner nos recommandations.',
      title: 'Matière',
      picker: {
        label: 'Matière',
        placeholder: 'Sélectionner une matière…',
        searchPlaceholder: 'Rechercher par nom ou désignation…',
        noResults: 'Aucune matière correspondante.',
        hint: 'Matière manquante ? Ajoutez-en une dans l\'onglet Matières de l\'Atelier.',
      },
      lot: {
        title: 'Détails du lot',
        optional: 'optionnel',
        supplierLabel: 'Fournisseur',
        supplierPlaceholder: 'Nom du fournisseur',
        lotLabel: 'Numéro de lot',
        lotPlaceholder: 'Ex. L2025-04',
        hint: 'Le même fournisseur et le même lot pour la même matière sont automatiquement dédupliqués côté serveur.',
      },
      empty: {
        title: 'Aucune matière',
        hint: 'Aucune matière — ajoutez-en une dans l\'onglet Matières de l\'Atelier.',
      },
      prefill: {
        header: 'Pré-rempli par le processus',
        locked: 'Verrouillé par le modèle de processus sélectionné. Choisissez un autre processus pour modifier.',
        lot: {
          supplier: 'Fournisseur',
          lot: 'Lot',
        },
      },
      errors: {
        required: 'Veuillez sélectionner une matière.',
      },
      supplierLabel: 'Fournisseur',
      supplierPlaceholder: 'Nom du fournisseur',
      lotLabel: 'Numéro de lot',
      lotPlaceholder: 'Ex. L2025-04',
    },
    machineTab: {
      sectionTitle: 'Machine utilisée',
      sectionDescription:
        'Sélectionnez une machine enregistrée ou complétez les informations manuellement.',
      dropdownLabel: 'Machine enregistrée',
      dropdownPlaceholder: 'Choisissez une machine',
      helperText:
        'Gérez votre bibliothèque de machines ou créez une nouvelle fiche depuis le gestionnaire. Ajustez ensuite les champs pour ce relevé précis.',
      detailsTitle: 'Détails machine pour ce relevé',
      brandLabel: 'Marque',
      brandPlaceholder: 'Ex. DMG Mori',
      modelLabel: 'Modèle',
      modelPlaceholder: 'Ex. DMC 65 monoBLOCK',
      serialLabel: 'Numéro de série / Identifiant',
      serialPlaceholder: 'Identifiant interne ou constructeur',
      kafkaNameLabel: 'Source des signaux',
      summaryTitle: 'Fiche sélectionnée',
      summarySerial: 'SN : {{serial}}',
      openManager: 'Ouvrir le gestionnaire de machines',
      refreshList: 'Rafraîchir la liste',
      listTitle: 'Machines enregistrées',
      listCount: '{{count}} élément(s)',
      emptyState: 'Aucune machine enregistrée pour le moment.',
      deleteSelection: 'Supprimer la sélection',
    },
    holderTab: {
      title: 'Porte-outil',
      description:
        "Identifiez le porte-outil utilisé pour suivre l'impact de la rigidité et de l'équilibrage sur la durée de vie de l'outil.",
      typeLabel: 'Type de porte-outil',
      brandLabel: 'Marque / Modèle',
      brandPlaceholder: 'Ex. REGO-FIX powRgrip',
      options: {
        hotShrink: 'Frettage à chaud',
        coldShrink: 'Frettage à froid',
        collet: 'Pince de serrage',
        hydraulic: 'Pince hydraulique',
        other: 'Autre',
      },
    },
    clampingTab: {
      title: 'Configuration de serrage',
      description:
        'Identifiez la méthode de maintien de la pièce pour interpréter les vibrations et anticiper les dérives.',
      typeLabel: 'Type de serrage',
      brandLabel: 'Marque / Modèle',
      brandPlaceholder: 'Ex. Hainbuch TOPlus mini',
      options: {
        vise: 'Étaux',
        clamp: 'Bride',
        fixture: 'Posage',
        chuck: 'Mandrin',
        rotarySteady: 'Canon tournant',
        fixedSteady: 'Canon fixe',
        collet: 'Pince',
        vacuum: 'Vacuum',
        glue: 'Colle',
        gel: 'Gel',
        other: 'Autre',
      },
    },
    lubricationTab: {
      title: 'Lubrification',
      description:
        "Précisez le mode et le produit utilisés pour suivre l'impact sur la durée de vie de l'outil.",
      typeLabel: 'Type de lubrification',
      brandLabel: 'Marque / Produit',
      brandPlaceholder: 'Ex. Blaser B-Cool',
      options: {
        dry: 'À sec',
        oil: 'Huile entière',
        emulsion: 'Émulsion',
        mql: 'Microlubrification',
        co2: 'CO₂',
        air: 'Air',
        other: 'Autre',
      },
    },
    breakTab: {
      stateTitle: 'État observé',
      stateDescription:
        "Décrivez l'état de l'outil au moment du relevé pour contextualiser le changement.",
      stateLabel: "État de l'outil",
      reasonTitle: 'Changement et suivi',
      reasonDescription:
        "Sélectionnez les raisons du remplacement et consignez une remarque pour garder une trace exploitable par l'atelier.",
      reasonLabel: 'Raison du changement',
      commentLabel: 'Remarque',
      commentPlaceholder: 'Note libre pour les équipes de production',
      stateOptions: {
        new: 'Neuf',
        worn: 'Usé',
        broken: 'Cassé',
        unknown: 'Inconnu',
      },
      reasonOptions: {
        completeBrokenTool: 'Outil cassé (complet)',
        brokenTeeth: 'Dent(s) cassée(s)',
        brokenPart: 'Pièce cassée',
        burr: 'Génération de bavure',
        badSurface: 'Mauvais état de surface',
        badDimension: 'Dimensions des pièces incorrectes',
        endOfSeries: 'Fin de série (plus de pièce à faire)',
        productionStop: 'Arrêt de production (stop dans la prod malgré une série en cours)',
        noise: "Bruit d'usinage",
        maxToolLife: 'Atteint la durée de vie maximum selon le process',
        partAppearance: 'Aspect de la pièce',
        vibration: 'Vibrations',
        other: 'Autre',
      },
    },
    uploadImage: {
      pageTitle: 'Importer une image',
      pageDescription: 'Importez une image contenant un code DataMatrix',
      breadcrumb: 'Import',
      dropZone: {
        prompt: 'Glissez-déposez une image ici',
        browse: 'Parcourir les fichiers',
        hint: 'Formats PNG, JPG et autres formats image',
      },
      statusIdle: 'Aucune image chargée',
      statusLoading: "Chargement de l'image…",
      errorDecode: "Erreur lors du décodage de l'image.",
      errorNoDimension: "Erreur : l'image ne contient aucune dimension.",
      statusScanning: 'Analyse en cours…',
      statusDetected: 'Identifiant reconnu : {{id}}',
      statusNotFound: 'Aucun DataMatrix détecté. Essayez une autre image.',
      imageAlt: 'Aperçu importé',
    },
    addToolModal: {
      title: 'Créer un nouvel outil',
      description: 'Enregistrez un nouvel outil dans la base de données avant de scanner.',
    },
    materials: {
      acier: 'Acier',
      acier_trempe: 'Acier trempé',
      aluminium: 'Aluminium',
      chrome: 'Chrome-Cobalt/Phynox/Stellite',
      titane: 'Titane',
      fonte: 'Fonte',
      nickel: 'Alliage Nickel',
      bronze: 'Bronze',
      laiton: 'Laiton',
      or: 'Or',
      super: 'Superalliages',
      argent: 'Argent',
      cube: 'CuBe',
      cuivre: 'Cuivre',
      durnico: 'Durnico',
      laiton_sans_plomb: 'Laiton sans plomb',
      metal_lourd: 'Métal lourd (tungstène)',
      céramique: 'Céramique',
      composite_carbone: 'Composite avec fibre de carbone',
      composite_verre: 'Composite avec fibre de verre',
      graphite: 'Graphite',
      palladium: 'Palladium',
      platine: 'Platine',
      polymere: 'Polymère',
      tantale: 'Tantale',
      autre: 'Autre',
      lt700: '<700 N/mm²',
      gt700: '>700 N/mm²',
      finemac: 'Finemac',
      inox: 'Inoxydable',
      lt58: 'Trempé <58HRc',
      lt65: 'Trempé <65HRc',
      lt8: '<8%Si',
      gt8: '>8%Si',
    },
    materialLotManager: {
      picker: {
        sectionTitle: 'Parcourir par matière',
        label: 'Matière',
        placeholder: 'Sélectionner une matière…',
        count: '{{count}} matières disponibles',
      },
      form: {
        sectionTitle: 'Trouver ou créer un lot',
        idempotent: 'Idempotent',
        fields: {
          supplier: {
            label: 'Fournisseur',
            placeholder: 'ex. Outokumpu',
            optional: '(optionnel)',
          },
          lotNumber: {
            label: 'Numéro de lot',
            placeholder: 'ex. L2025-04-001',
            optional: '(optionnel)',
          },
        },
        idempotencyHint:
          'Le même fournisseur et numéro de lot pour cette matière renvoient toujours le même identifiant de lot.',
        submit: {
          findOrCreate: 'Trouver ou créer',
          saving: 'Enregistrement…',
          reset: 'Réinitialiser',
        },
        result: {
          title: 'Lot prêt',
          idLabel: 'id #{{id}}',
          noSupplier: 'Sans fournisseur',
        },
      },
      list: {
        title: 'Lots pour {{name}}',
        count: '{{count}} lots',
        loading: 'Chargement des lots',
        emptyTitle: 'Aucun lot pour l\'instant',
        emptyHint: 'Créez-en un à l\'aide du formulaire à gauche.',
        row: {
          noSupplier: 'Sans fournisseur',
        },
      },
      empty: {
        title: 'Sélectionnez une matière pour voir ses lots',
        hint: 'Les lots sont liés à une seule matière — choisissez-en une dans la liste déroulante ci-dessus pour commencer à ajouter ou consulter des lots fournisseurs.',
      },
      errors: {
        loadFailed: 'Impossible de charger les lots pour cette matière.',
        saveFailed: 'Impossible d\'enregistrer le lot. Veuillez réessayer.',
      },
    },
    workshop: {
      hero: {
        badge: 'Atelier',
        title: 'Atelier',
        description: "Enregistrez des outils dans la base de données et gérez vos machines.",
        breadcrumb: 'Atelier',
      },
      tabs: {
        tool: "Enregistrer un outil",
        machines: 'Machines',
        articles: 'Articles',
        materials: 'Matières',
        lots: 'Lots matière',
        processes: 'Processus',
      },
      toolForm: {
        mode: {
          single: 'Individuel',
          batch: 'Lot',
        },
        single: {
          sectionTitle: "Informations sur l'outil",
        },
        batch: {
          sectionTitle: 'Enregistrement en lot',
          addToolId: 'Ajouter un ID outil',
          toolIdPlaceholder: 'ID outil #{{n}}',
        },
        fields: {
          toolId: {
            label: 'ID outil',
            placeholder: 'ex. EGO-001',
          },
          reference: {
            label: 'Référence',
            placeholder: 'ex. LB-12345',
          },
          company: {
            label: 'Entreprise',
            placeholder: "Nom de l'entreprise",
            autoFilled: 'Rempli automatiquement',
          },
        },
        info: {
          single:
            'Crée un nouvel outil dans la base de données. Disponible immédiatement pour les scans de traçabilité.',
          batch:
            'Crée plusieurs outils partageant la même référence. Les résultats sont affichés par ID outil.',
        },
        submit: {
          single: "Créer l'outil",
          batch: 'Créer {{count}} outils',
        },
        success: 'Outil créé avec succès.',
        errors: {
          default: "Échec de la création de l'outil.",
        },
        scan: {
          button: 'Scanner DataMatrix',
          buttonNext: 'Scanner suivant',
          buttonRescan: 'Re-scanner',
          connecting: 'Connexion…',
          scanning: 'Scan en cours…',
          cancel: 'Annuler',
          waitingOverlay: 'En attente du DataMatrix — présentez l\'outil devant la caméra',
          statusDisconnected: 'Keyence non connecté',
          statusConnected: 'Connecté · {{ip}}',
          statusDetected: 'DataMatrix détecté — vérifiez l\'ID ci-dessus',
          statusReady: 'Prêt pour le scan suivant',
          successFlash: '{{toolId}} enregistré avec succès',
        },
        session: {
          title: 'Cette session',
          count: '{{n}} enregistré(s)',
          clear: 'Effacer',
        },
        autoRegister: {
          label: 'Enregistrement automatique',
          description: 'Scan → enregistrement → scan suivant automatiquement — aucun clic entre les outils',
          registering: 'Enregistrement…',
          registeringFlash: 'Enregistrement de {{toolId}}…',
        },
      },
    },
  },
};
